import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Edit2, Loader2, Menu, Plus, Tags } from 'lucide-react';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import CategoryIcon from '@/components/pos/CategoryIcon';
import CategoryFormModal from '@/components/pos/CategoryFormModal';
import { getDefaultCategoryColor } from '@/constants/posCategoryIcons';
import { useUIStore } from '@/store/uiStore';
import { posApi, storefrontAdminApi } from '@/services/api';
import { resolveFileUrl } from '@/utils/fileUrl';
import type { POSCategory } from '@/types';

export default function CategoriesPage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<POSCategory | null>(null);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categoriesList'],
    queryFn: async () => {
      const res = await posApi.getCategories();
      return res.data.message as POSCategory[];
    },
  });

  const { data: storefrontOn } = useQuery({
    queryKey: ['storefrontEnabledForCategories'],
    queryFn: async () => {
      try {
        const res = await storefrontAdminApi.getSettings();
        return Boolean(res.data.message?.enable_storefront);
      } catch {
        return false;
      }
    },
    staleTime: 60_000,
  });

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <TrialBanner />
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 flex-wrap">
          <button type="button" onClick={toggleSidebar} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Tags className="w-5 h-5 text-primary-600" />
              {t('categories.title')}
            </h1>
            <p className="text-sm text-gray-500">{t('categories.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setIsModalOpen(true);
            }}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            {t('categories.add')}
          </button>
        </header>

        <div className="p-4 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-gray-600">
                  <thead className="bg-gray-50 text-gray-900 font-medium border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4">{t('categories.name')}</th>
                      <th className="px-6 py-4">{t('categories.type')}</th>
                      <th className="px-6 py-4">{t('categories.displayOrder')}</th>
                      <th className="px-6 py-4">{t('categories.icon')} / {t('categories.color')}</th>
                      {storefrontOn ? (
                        <th className="px-6 py-4">{t('categories.storeBanner')}</th>
                      ) : null}
                      <th className="px-6 py-4 text-right">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {categories.map((cat) => {
                      const color = cat.pos_category_color || getDefaultCategoryColor(cat.category_name);
                      return (
                        <tr key={cat.name} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <CategoryIcon
                                icon={cat.pos_category_icon}
                                image={cat.pos_category_image}
                                color={color}
                                size="sm"
                              />
                              <span className="font-medium text-gray-900">{cat.category_name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                cat.category_type === 'Service'
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {cat.category_type || 'Product'}
                            </span>
                          </td>
                          <td className="px-6 py-4">{cat.display_order}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span
                                className="w-4 h-4 rounded-full border border-gray-200 shrink-0"
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                              <span className="text-xs text-gray-500 truncate max-w-[10rem]">
                                {cat.pos_category_icon ||
                                  (cat.pos_category_image ? t('categories.image') : t('common.none'))}
                              </span>
                            </div>
                          </td>
                          {storefrontOn ? (
                            <td className="px-6 py-4">
                              {cat.storefront_banner ? (
                                <img
                                  src={resolveFileUrl(cat.storefront_banner)}
                                  alt=""
                                  className="h-10 w-20 object-cover rounded-md border border-gray-200"
                                />
                              ) : (
                                <span className="text-xs text-gray-400">{t('common.none')}</span>
                              )}
                            </td>
                          ) : null}
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditing(cat);
                                  setIsModalOpen(true);
                                }}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title={t('categories.edit')}
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {categories.length === 0 && (
                      <tr>
                        <td colSpan={storefrontOn ? 6 : 5} className="px-6 py-8 text-center text-gray-500">
                          {t('categories.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <CategoryFormModal
          editing={editing}
          onClose={() => {
            setIsModalOpen(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
